import { Component, OnInit } from '@angular/core';

import { ChartDataset, ChartOptions, ChartType } from 'chart.js';
import { ActivatedRoute } from '@angular/router';
import { NgxUiLoaderService } from 'ngx-ui-loader';

import { Globals } from '../../../_models/globals';

import { NotificationService } from '../../../_services/notification.service';
import { SellerDadosCockpitService } from './dados-cockpit.service';
import { ApiResponseModel } from 'app/_models/apiResponseModel';
import { SellerCockpitGetResponseModel } from 'app/seller/_models/sellerCockpitGetResponseModel';
import { SellerCockpitGetByMonthResponseModel } from 'app/seller/_models/sellerCockpitGetByMonthResponseModel';

declare const isEmpty: any;
declare const obterNomeMesAno: any;

@Component({
    selector: 'app-dados-cockpit', templateUrl: './dados-cockpit.component.html', styleUrls: ['./dados-cockpit.component.scss'],
    standalone: false
})
export class SellerDadosCockpitComponent implements OnInit
{
    public barChartOptions: ChartOptions;
    public barChartType: ChartType = 'bar';
    public barChartLegend = true;

    public barChartData: ChartDataset[];
    public barChartLabels: string[];

    cockpit: any;
    is_resposta_automatica: boolean;
    loading: boolean;
    total_last_30_days: number;
    total_current_month: number;
    translation: any;

    constructor(private notificationService: NotificationService, private cockpitService: SellerDadosCockpitService, private globals: Globals, private activatedRoute: ActivatedRoute, private ngxLoader: NgxUiLoaderService)
    {
        this.activatedRoute.data.subscribe(() =>
        {
            this.translation = {
                grafico: {
                    market_share: { titulo: this.globals.translate('seller.cockpit.grafico.market-share.titulo'), y: this.globals.translate('seller.cockpit.grafico.market-share.y') },
                    vendas: { titulo: this.globals.translate('seller.cockpit.grafico.vendas.titulo'), y: this.globals.translate('seller.cockpit.grafico.vendas.y') }
                }
            };
        });

        this.barChartData = [{ data: [] }];
        this.barChartLabels = [];

        this.loading = false;
    }

    ngOnInit()
    {
        this.formatarGrafico();
        this.carregarTela(0);
    }

    carregarTela(nAttempts: number)
    {
        this.ngxLoader.startLoader('loader-principal');

        this.cockpitService.get()
            .subscribe({
                next: (response: ApiResponseModel<SellerCockpitGetResponseModel>) =>
                {
                    if (response != null)
                    {
                        this.cockpit = response.result.cockpit;
                        this.is_resposta_automatica = response.result.isRespostaAutomatica;
                        let total_sales = response.result.totalSales;

                        this.cockpit.map(c =>
                        {
                            c.anoMes = parseInt(c.ano_numerico.toString() + c.mes_numerico.toString().padStart(2, '0'));
                            c.mes = obterNomeMesAno(c.anoMes);
                        });

                        this.montarGraficoLabels(this.cockpit);
                        this.montarGraficoDados(this.cockpit);
                        this.montarTotais(total_sales);
                    }

                    this.ngxLoader.stopLoader('loader-principal');
                },
                error: error =>
                {
                    nAttempts = nAttempts || 1;
                    console.log(error, nAttempts);

                    if (nAttempts >= 5)
                    {
                        this.ngxLoader.stopLoader('loader-principal');
                        this.notificationService.showErrorToastr(this.globals.translate('seller.cockpit.mensagens.erro.carregamento.corpo'));
                        return;
                    }

                    this.carregarTela(++nAttempts);
                }
            });
    }

    carregarDetalheMes(cockpit_mes, nAttempts: number)
    {
        if (!isEmpty(cockpit_mes.detalhes))
        {
            cockpit_mes.visivel = !cockpit_mes.visivel;
            return;
        }

        this.loading = true;

        let ano = cockpit_mes.ano_numerico;
        let mes = cockpit_mes.mes_numerico;

        this.cockpitService.getByMonth(ano, mes)
            .subscribe({
                next: (response: ApiResponseModel<SellerCockpitGetByMonthResponseModel>) =>
                {
                    if (response != null)
                    {
                        cockpit_mes.detalhes = response.result.cockpitMonth;
                    }

                    cockpit_mes.visivel = true;
                    this.loading = false;
                },
                error: error =>
                {
                    nAttempts = nAttempts || 1;
                    console.log(error, nAttempts);

                    if (nAttempts >= 5)
                    {
                        cockpit_mes.visivel = false;
                        this.loading = false;
                        this.notificationService.showErrorToastr(this.globals.translate('seller.cockpit.mensagens.erro.carregamento-detalhe.corpo'));
                        return;
                    }

                    this.carregarDetalheMes(cockpit_mes, ++nAttempts);
                }
            });
    }

    formatarGrafico()
    {
        let vendas_label = this.translation.grafico.vendas.y;
        let ms_label = this.translation.grafico.market_share.y;

        this.barChartOptions =
        {
            responsive: true,
            maintainAspectRatio: false,
            elements: { line: { fill: false, tension: .25 } },
            scales:
            {
                'vendas': {
                    position: 'left',
                    title: { display: true, text: vendas_label },
                    ticks: { callback: function (value, index, values) { return 'R$ ' + Intl.NumberFormat().format((parseFloat(value.toString()))); } }
                },
                'market_share': {
                    position: 'right',
                    title: { display: true, text: ms_label },
                    ticks: { callback: function (value, index, values) { return Intl.NumberFormat().format((parseFloat(value.toString()))) + '%'; } }
                }
            },
            plugins: {
                tooltip: {
                    callbacks: {
                        label: function (context)
                        {
                            var datasetLabel = context.dataset.label || '';

                            if (context.datasetIndex === 1)
                                return datasetLabel + ': R$ ' + Intl.NumberFormat().format((parseFloat(context.parsed.y.toString())));
                            else
                                return datasetLabel + ': ' + Intl.NumberFormat().format((parseFloat(context.parsed.y.toString()))) + '%';
                        }
                    }
                }
            }
        };
    }

    montarGraficoDados(lista)
    {
        let vendas_label = this.translation.grafico.vendas.titulo;
        let ms_label = this.translation.grafico.market_share.titulo;

        let vendas = lista.map(i => i.vendas);
        let mshare = lista.map(i => i.marketshare);

        this.barChartData = [
            { data: mshare, label: ms_label, type: 'line', borderColor: '#FE5D15', borderWidth: 1, pointBackgroundColor: '#FE5D15', pointHoverBackgroundColor: 'transparent', yAxisID: 'market_share' },
            { data: vendas, label: vendas_label, stack: 'a', backgroundColor: '#532D4A', borderColor: '#532D4A', hoverBackgroundColor: '#3A032D', yAxisID: 'vendas' }
        ];
    }

    montarGraficoLabels(lista)
    {
        this.barChartLabels = [];

        lista.forEach(item =>
        {
            this.barChartLabels.push(obterNomeMesAno(item.anoMes))
        });
    }

    montarTotais(total_sales)
    {
        if (total_sales.length == 0 || isEmpty(total_sales[0].total_current_month))
            this.total_current_month = 0;
        else
        {
            this.total_current_month = 0;

            for (var i = 0; i < total_sales.length; i++)
            {
                this.total_current_month += total_sales[i].total_current_month;
            }
        }

        this.total_last_30_days = 0;

        if (total_sales && total_sales.length > 0)
        {
            for (var i = 0; i <= total_sales.length - 1; i++)
            {
                if (!isEmpty(total_sales[i].total_last_30_days))
                    this.total_last_30_days += total_sales[i].total_last_30_days;
            }
        }
    }
}
